import React, { useMemo, useCallback, useContext, createContext } from 'react'
import { useRouter } from 'next/router'
import type { SelectedCoinDenom } from '@/wallet-utils/types'

const DEFAULT_DENOM: SelectedCoinDenom = 'ATOM'

type SelectedDenomContextValue = [SelectedCoinDenom, (d: SelectedCoinDenom) => void]

const Context = createContext<SelectedDenomContextValue | null>(null)

const SelectedDenomContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const router = useRouter()

  const denom = useMemo<SelectedCoinDenom>(() => {
    const q = router.query.chain
    if (!q || Array.isArray(q)) return DEFAULT_DENOM
    return (q as SelectedCoinDenom) || DEFAULT_DENOM
  }, [router.query])

  const setDenom = useCallback(
    (next: SelectedCoinDenom) => {
      router.push('/?chain=' + next, undefined, { shallow: true })
    },
    [router]
  )

  const value = useMemo(
    () => [denom, setDenom] as [SelectedCoinDenom, (d: SelectedCoinDenom) => void],
    [denom, setDenom]
  )

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { SelectedDenomContextProvider as SelectedDenomProvider }

export const useSelectedDenom = (): SelectedDenomContextValue => {
  const ctx = useContext(Context)
  if (!ctx) throw new Error('useSelectedDenom must be used within SelectedDenomProvider')
  return ctx
}
